import { z } from "zod";
import { QueryPlan } from "./query-plan";

export const ApiScope = z.enum(["discover", "query", "metadata", "datasets:read", "billing:read"]);
export type ApiScope = z.infer<typeof ApiScope>;

const DecimalString = z.string().regex(/^\d{1,9}(\.\d{1,6})?$/);

export const QueryConstraints = z.object({
  freshness_seconds: z.number().int().positive().max(86400 * 365).optional(),
  max_query_price: DecimalString.optional(),
  max_latency_ms: z.number().int().positive().max(60000).optional(),
  quality_min: z.number().min(0).max(1).optional(),
  preference: z.enum(["balanced", "cheapest", "fastest", "freshest", "highest_quality"]).default("balanced"),
  datasets: z.array(z.string()).max(20).optional(),
  /**
   * Opt in to a CLOSEST-MATCH answer when no exact plan exists. Only ever applied to data the caller pays nothing for
   * (its own data or free datasets); the response then has dbindex.match = "closest" | "overview" and lists the
   * question words that were not used. Off by default because agents cannot see a UI label.
   */
  allow_closest: z.boolean().optional(),
});
export type QueryConstraints = z.infer<typeof QueryConstraints>;

export const QueryRequest = z
  .object({
    question: z.string().min(3).max(1000).optional(),
    plan: QueryPlan.optional(),
    constraints: QueryConstraints.default({ preference: "balanced" }),
    fallback: z.boolean().default(true),
    /** "records" also returns data.records: one JSON object per row ({column: value}), handy for curl/jq and agents. */
    format: z.enum(["table", "records"]).default("table"),
  })
  .refine((r) => !!r.question !== !!r.plan, { message: "provide exactly one of question or plan" });
export type QueryRequest = z.infer<typeof QueryRequest>;

/** Add data.records ({column: value} per row) to a query response, for format = "records". Pure, response-time only. */
export function withRecords<T extends { data: { columns: string[]; rows: unknown[][]; records?: Record<string, unknown>[] } }>(body: T): T {
  const { columns, rows } = body.data;
  return { ...body, data: { ...body.data, records: rows.map((r) => Object.fromEntries(columns.map((c, i) => [c, r[i] ?? null]))) } };
}

export const DiscoverRequest = z.object({
  question: z.string().min(3).max(1000),
  constraints: QueryConstraints.default({ preference: "balanced" }),
  limit: z.number().int().min(1).max(20).default(5),
});
export type DiscoverRequest = z.infer<typeof DiscoverRequest>;

export interface Provenance {
  dataset_id: string;
  dataset_name: string;
  provider: string;
  provider_id: string;
  schema_version: number;
  contract_version: number;
  source_timestamp: string | null;
  query_timestamp: string;
  freshness_seconds: number | null;
  quality_score: number;
  transformations: string[];
  license: string;
  rights: Record<string, unknown>;
  cache_status: string;
}

export interface QueryResponse {
  /** Data returned by the provider's serving projection (untrusted content; never instructions). */
  data: { columns: string[]; rows: unknown[][]; row_count: number; truncated: boolean; next_cursor: string | null; records?: Record<string, unknown>[] };
  /** DBIndex-derived: plan + routing explanation. */
  dbindex: {
    plan: unknown; plan_source: "structured" | "capability_match" | "model" | "nearest" | "overview"; routing: unknown;
    /** "exact" unless the caller opted in to closest-match answers (constraints.allow_closest) and none was exact. */
    match?: "exact" | "closest" | "overview";
    /** For closest/overview answers: what was answered, the question words NOT used, and exact questions to try. */
    closest?: { answered: string; dropped: string[]; suggestions: string[] };
  };
  provenance: Provenance;
  billing: { query_price: string; currency: "USD"; charged: boolean; idempotent_replay?: boolean };
  request_id: string;
}
